import React, { useState, useEffect, useCallback } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormCheckbox from "../../form-components/CheckboxComponent";
import FormSelect from "../../form-components/SelectComponent";
import Swal from "sweetalert2";
import Content from "../../../layout/content/Content";
import { Block, DataTable, Icon, Button } from "../../../components/Component";
import { Row, Col } from "reactstrap";
import checklistApis from "../../../api/checklist/checklistapi";
import QuestionsCreation, { Question } from "./QuestionsCreation";
import { useNavigate } from "react-router-dom";
interface ChecklistOption {
  value: string;
  label: string;
}
interface FormData {
  checklistname: string;
  clone: boolean;
  checklistId: string;
}

const CreateChecklist: React.FC = () => {
  const navigate = useNavigate();
  const methods = useForm<FormData>({
    defaultValues: {
      checklistname: "",
      clone: false,
      checklistId: "",
    },
  });
  const [questions, setQuestions] = useState<Question[]>([]);
  const [checklistOptions, setChecklistOptions] = useState<ChecklistOption[]>([]);
  const [isLoadingChecklists, setIsLoadingChecklists] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [customQuestionsAdded, setCustomQuestionsAdded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cloneCheckboxWatch = useWatch({
    control: methods.control,
    name: "clone",
  });
  const selectedChecklistId = useWatch({
    control: methods.control,
    name: "checklistId",
  });

  const loadChecklistOptions = async () => {
    try {
      setIsLoadingChecklists(true);
      const response = await checklistApis.fetchDropdown();

      const options = response.data.map((item: any) => ({
        value: item.checklistId,
        label: item.checklistName,
      }));

      setChecklistOptions(options);
    } catch (error) {
      console.error("Error loading checklist options:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load checklist options",
        icon: "error",
      });
    } finally {
      setIsLoadingChecklists(false);
    }
  };

  const loadQuestionsForChecklist = async (checklistId: string) => {
    try {
      setIsLoadingQuestions(true);
      const response = await checklistApis.fetchChecklistById(checklistId);

      if (!response.data) {
        throw new Error("No data in response");
      }

      const questionsData = response?.data?.checklistQuestions || [];


      const transformedQuestions = questionsData?.map((q: any, index: number) => {
        return {
          id: q.questionId?.toString(),
          name: q.questionName,
          type: q.questionType,
          options: q?.optionsType ? Object?.values(q?.optionsType).join(', ') : "",
          mandatory: q.isMandatory || false,
          description: q.description || '',
        }
      });


      setQuestions(transformedQuestions);
      setCustomQuestionsAdded(false);
    } catch (error) {
      console.error("Error loading questions:", error);
      setCustomQuestionsAdded(false);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const handleCloneCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    methods.setValue("clone", checked);

    if (checked) {
      loadChecklistOptions();
    } else {
      methods.setValue("checklistId", "");
      setQuestions([]);
      setCustomQuestionsAdded(false);
    }
  }, []);

  const handleQuestionsUpdate = (updatedQuestions: Question[]) => {
    try {
      setQuestions(updatedQuestions);
      const hasCustomQuestions = updatedQuestions.some(q => q.id?.includes("question-"));
      setCustomQuestionsAdded(hasCustomQuestions);
    } catch (error) {
      console.error("Error updating questions:", error);
    }
  }

  const handleBack = () => {
    navigate("/pms/Checklist/checklist-lists");
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const formData = methods.getValues();

      const payload = {
        checklistName: formData.checklistname,
        isUserDefined: 0,
        questions: formatQuestionsForApi(questions),
      };

      const response = await checklistApis.createChecklist(payload);
      Swal.fire({
        icon: "success",
        title: "Checklist Created",
        text: "Your checklist has been successfully created.",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/pms/Checklist/checklist-lists");
      });
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "Failed to submit checklist. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatQuestionsForApi = (questions: Question[]): any[] => {
    return questions.map((question) => {
      let optionsType = {};
      if (question.type !== "Text" && question.options) {
        const optionsArray = question.options.split(',').map(opt => opt.trim());
        optionsType = optionsArray.reduce((acc, option, index) => {
          const key = String.fromCharCode(65 + index); // using A, B, C... for payload keys
          return { ...acc, [key]: option };
        }, {});
      }

      return {
        questionName: question.name,
        questionType: question.type.toLowerCase().replace(' ', '_'),
        optionsType: Object.keys(optionsType).length > 0 ? optionsType : undefined,
        isMandatory: question.mandatory,
        description: question.description || undefined,
      };
    });
  };

  const confirmSubmit = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to submit this checklist?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit!",
      cancelButtonText: "No, cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        handleSubmit();
      }
    });
  };

  useEffect(() => {
    if (selectedChecklistId && cloneCheckboxWatch) {
      const loadQuestions = async () => {
        try {
          await loadQuestionsForChecklist(selectedChecklistId);
        } catch (error) {
          console.error("Failed to load questions:", error);
          methods.setValue("checklistId", "");
        }
      };
      loadQuestions();
    } else if (!selectedChecklistId && cloneCheckboxWatch) {
      setQuestions([]);
    }
  }, [selectedChecklistId, cloneCheckboxWatch]);

  return (
    <Content>
      <Block className="add-modal-container font-regular">
        <FormProvider {...methods}>
          <DataTable
            className="card-stretch fixed-width-table"
            style={{ border: 'none' }}
            bodyClassName=""
            title=""
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px', fontSize: '20px' }}>
              <h3 style={{ fontSize: '25px' }}> New Checklist</h3>
              <Button
                className="btn btn-primary btn-l"
                color="primary"
                size="sm"
                onClick={handleBack}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </div>
            <form>
              <div className="card-inner position-relative" style={{ paddingTop: '5px' }}>
                <div className="row g-2">
                  <div className="col-5">
                    <FormInput
                      name="checklistname"
                      label="Checklist Title"
                      required={true}
                      type="text"
                      placeholder="Enter Checklist Title"
                    />
                    <FormCheckbox
                      name="clone"
                      optionLabel="Clone From Existing Checklists"
                      noGap={true}
                      size={"small"}
                      onChange={handleCloneCheckboxChange}
                      className="mt-1"
                      disabled={customQuestionsAdded}
                    />
                  </div>
                  {cloneCheckboxWatch && (
                    <div className="col-5">
                      <FormSelect
                        name="checklistId"
                        label=" Search Checklists"
                        required={true}
                        placeholder={
                          isLoadingChecklists ? "Loading checklists..." : "Select Checklist"
                        }
                        options={checklistOptions}
                        disabled={customQuestionsAdded || isLoadingChecklists}
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>

            {isLoadingQuestions && (
              <div className="text-center py-4">
                <Icon name="spinner" className="spin" /> Loading questions...
              </div>
            )}

            {!isLoadingQuestions && (
              <>
                <QuestionsCreation
                  initialQuestions={questions}
                  onQuestionsUpdate={handleQuestionsUpdate}
                  showHeading={false}
                />

                <Row className="mt-4 mx-4 mb-5">
                  <Col lg="12">
                    <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                      <li>
                        <Button
                          color="primary"
                          className="btn-dim btn btn-secondary"
                          onClick={handleBack}
                        >
                          Cancel
                        </Button>
                      </li>
                      <li>
                        <Button
                          className="btn btn-primary btn-md"
                          type="button"
                          color="primary"
                          onClick={confirmSubmit}
                          disabled={isSubmitting || questions.length === 0}
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </>
            )}
          </DataTable>
        </FormProvider>
      </Block>
    </Content>
  );
};

export default CreateChecklist;